import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import { Container, Div, Icon, Notification, Text } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { Page } from 'src/containers';
import { bcActions, composerActions } from 'src/store/actions';
import { Canvas, ButtonGroup, Button } from 'src/components';

const slidesFilter = ({ slides }) => slides;

const isValidSelector = createSelector([slidesFilter], (slides) =>
  slides.reduce(
    (isValid, slide) =>
      isValid &&
      slide.view &&
      (slide.segmentIds.length > 0 || CONSTANTS.VIEWS[slide.view].maxSegments === 0) &&
      (slide.view !== 'IMAGE' || slide.imageFile || slide.imageUrl),
    true
  )
);

function Composer({
  history,
  match: {
    params: { datasetId: datasetIdParam },
  },
  app: { tenant },
  composer: {
    fetchSegmentsStatus,
    segments,
    storyId,
    slides,
    selectedSlide,
    dirty,
    addStoryStatus,
    addStoryError,
    editStoryStatus,
    editStoryError,
    deleteStoryStatus,
    deleteStoryError,
  },
  isValid,
  fetchSegments,
  fetchStories,
  addSlide,
  removeSlide,
  moveSlide,
  selectSlide,
  selectView,
  selectSegmentIds,
  selectImageFile,
  addStory,
  editStory,
  deleteStory,
  setDatasetId,
  setDatasetName,
}) {
  const [hasNotification, showNotification] = useState(false);
  const [message, setMessage] = useState('');

  /**
   * Once data is loaded, set breadcrumb details
   */
  useEffect(() => {
    if (segments && segments.length > 0 && fetchSegmentsStatus === CONSTANTS.API.SUCCEEDED) {
      setDatasetId(segments[0].dataset.id);
      setDatasetName(segments[0].dataset.name);
    }
  }, [segments, fetchSegmentsStatus, setDatasetId, setDatasetName]);

  useEffect(() => {
    if (addStoryStatus === CONSTANTS.API.FAILED) {
      showNotification(true);
      setMessage(`Unable to save story. Error is ${addStoryError}`);
    }
    if (editStoryStatus === CONSTANTS.API.FAILED) {
      showNotification(true);
      setMessage(`Unable to save story. Error is ${editStoryError}`);
    }
    if (addStoryStatus === CONSTANTS.API.FAILED) {
      showNotification(true);
      setMessage(`Unable to preview story. Error is ${deleteStoryError}`);
    }
    if (addStoryStatus === CONSTANTS.API.SUCCEEDED || editStoryStatus === CONSTANTS.API.SUCCEEDED) {
      showNotification(true);
      setMessage('Story successfully saved');
      setTimeout(() => {
        showNotification(false);
        setMessage('');
      }, 2000);
    }
    if (deleteStoryStatus === CONSTANTS.API.SUCCEEDED) {
      showNotification(true);
      setMessage('Story successfully deleted');
      setTimeout(() => {
        showNotification(false);
        setMessage('');
        history.push('/datasets');
      }, 2000);
    }
  }, [addStoryStatus, addStoryError, editStoryStatus, editStoryError, deleteStoryStatus, deleteStoryError, history]);

  const getSegmentsData = useCallback(
    (datasetId) => {
      const query = {
        filters: [
          {
            and: [{ name: 'dataset_id', op: 'eq', val: datasetId }],
          },
        ],
      };
      fetchSegments(query);
    },
    [fetchSegments]
  );

  const getStoriesData = useCallback(
    (datasetId) => {
      const query = {
        filters: [
          {
            and: [{ name: 'dataset_id', op: 'eq', val: datasetId }],
          },
        ],
        order_by: [{ field: 'id', direction: 'desc' }],
      };
      fetchStories(query);
    },
    [fetchStories]
  );

  const views = CONSTANTS.VIEWS.ALL.map((code) => CONSTANTS.VIEWS[code]);

  /*
   * Fetch data whenever the tenant or params change
   */
  useEffect(() => {
    if (tenant) {
      getSegmentsData(datasetIdParam);
      getStoriesData(datasetIdParam);
    }
  }, [tenant, datasetIdParam, getSegmentsData, getStoriesData]);

  return (
    <Container>
      <Notification
        w="24rem"
        isOpen={hasNotification}
        suffix={
          <Icon
            color="white"
            name="Cross"
            pos="absolute"
            top="1.5rem"
            right="1rem"
            size="16px"
            cursor="pointer"
            onClick={() => showNotification(false)}
          />
        }
        position="fromTopRight"
        rounded="md"
        textWeight={400}
      >
        <Text m="2rem" p={{ l: '0.5rem', t: '0.25rem' }} textSize="subheader">
          {message}
        </Text>
      </Notification>
      <Div p={{ y: '1rem' }} d="flex" align="center" justify="flex-end">
        <Button
          buttonStyle="primary"
          m={{ l: '1rem' }}
          disabled={!storyId}
          onClick={() => {
            deleteStory(storyId);
          }}
        >
          Delete
        </Button>
        <Button
          buttonStyle="primary"
          m={{ l: '1rem' }}
          disabled={!storyId || dirty}
          onClick={() => {
            history.push(`/story/${datasetIdParam}`);
          }}
        >
          Preview
        </Button>
        <Button
          buttonStyle="primary"
          m={{ l: '1rem' }}
          disabled={!isValid || !dirty}
          onClick={() => {
            storyId
              ? editStory(storyId, datasetIdParam, 'Default Story', slides)
              : addStory(datasetIdParam, 'Default Story', slides);
          }}
        >
          {dirty ? (
            <p>
              Save
              <sup>*</sup>
            </p>
          ) : (
            <p>Save</p>
          )}
        </Button>
      </Div>
      <Text textSize="subheader" p={{ t: '1rem' }}>
        Slides
      </Text>
      <Canvas
        slides={slides}
        segments={segments}
        selectImageFile={selectImageFile}
        addSlide={addSlide}
        removeSlide={removeSlide}
        moveSlide={moveSlide}
        selectedSlide={selectedSlide}
        onSelect={(index) => {
          selectSlide(index);
        }}
      />
      <Text textSize="subheader" p={{ t: '1rem' }}>
        Select View
      </Text>
      <ButtonGroup
        selected={[slides[selectedSlide].view]}
        maxSelection={1}
        options={views.map((view) => ({
          id: view.code,
          name: view.name,
          value: view.code,
        }))}
        onClick={(values) => {
          selectView(selectedSlide, values[0]);
        }}
      />
      <Text textSize="subheader" p={{ t: '1rem' }}>
        Select Segment
      </Text>
      {fetchSegmentsStatus === CONSTANTS.API.SUCCEEDED ? (
        <ButtonGroup
          selected={slides[selectedSlide].segmentIds}
          options={segments.map((segment) => ({
            id: segment.id,
            name: segment.filter === 'POPULATION_SNAPSHOT' ? segment.dataset.name : segment.name,
            value: segment.id,
            theme:
              segment.filter === 'POPULATION_SNAPSHOT'
                ? 'info'
                : segment.filter && !segment.filter.startsWith('CLUSTERING')
                ? 'gray'
                : 'success',
          }))}
          maxSelection={slides[selectedSlide].view ? CONSTANTS.VIEWS[slides[selectedSlide].view].maxSegments : 1}
          onClick={(values) => {
            selectSegmentIds(selectedSlide, values);
          }}
        />
      ) : null}
    </Container>
  );
}

const mapStateToProps = ({ app, composer }) => ({
  app,
  composer,
  isValid: isValidSelector(composer),
});

const mapDispatchToProps = (dispatch) => ({
  setDatasetId: (datasetId) => dispatch(bcActions.setDatasetId(datasetId)),
  setDatasetName: (datasetName) => dispatch(bcActions.setDatasetName(datasetName)),
  fetchStories: (query) => dispatch(composerActions.fetchStories(query)),
  fetchSegments: (query) => dispatch(composerActions.fetchSegments(query)),
  addSlide: () => dispatch(composerActions.addSlide()),
  removeSlide: (index) => dispatch(composerActions.removeSlide(index)),
  moveSlide: (fromIndex, toIndex) => dispatch(composerActions.moveSlide(fromIndex, toIndex)),
  selectSlide: (index) => dispatch(composerActions.selectSlide(index)),
  selectView: (index, viewIndex, view) => dispatch(composerActions.selectView(index, viewIndex, view)),
  selectSegmentIds: (index, segmentIds) => dispatch(composerActions.selectSegmentIds(index, segmentIds)),
  selectImageFile: (index, imageFile) => dispatch(composerActions.selectImageFile(index, imageFile)),
  addStory: (datasetId, name, slides) => dispatch(composerActions.addStory(datasetId, name, slides)),
  editStory: (storyId, datasetId, name, slides) =>
    dispatch(composerActions.editStory(storyId, datasetId, name, slides)),
  deleteStory: (storyId) => dispatch(composerActions.deleteStory(storyId)),
});

Composer.propTypes = {
  composer: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        pxScores: PropTypes.arrayOf(PropTypes.shape({})),
        dataset: PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      })
    ).isRequired,
    fetchSegmentsStatus: PropTypes.string.isRequired,
    storyId: PropTypes.oneOf(PropTypes.number, undefined),
    slides: PropTypes.arrayOf(
      PropTypes.shape({
        view: PropTypes.oneOf(PropTypes.string, undefined),
        segmentIds: PropTypes.arrayOf(PropTypes.shape({})),
      })
    ).isRequired,
    selectedSlide: PropTypes.number.isRequired,
    dirty: PropTypes.bool.isRequired,
    addStoryStatus: PropTypes.string.isRequired,
    addStoryError: PropTypes.oneOf(PropTypes.string, undefined),
    editStoryStatus: PropTypes.string.isRequired,
    editStoryError: PropTypes.oneOf(PropTypes.string, undefined),
    deleteStoryStatus: PropTypes.string.isRequired,
    deleteStoryError: PropTypes.oneOf(PropTypes.string, undefined),
  }).isRequired,
  app: PropTypes.shape({
    tenant: PropTypes.string.isRequired,
  }).isRequired,
  isValid: PropTypes.bool.isRequired,
  fetchSegments: PropTypes.func.isRequired,
  fetchStories: PropTypes.func.isRequired,
  addSlide: PropTypes.func.isRequired,
  removeSlide: PropTypes.func.isRequired,
  moveSlide: PropTypes.func.isRequired,
  selectSlide: PropTypes.func.isRequired,
  selectView: PropTypes.func.isRequired,
  selectSegmentIds: PropTypes.func.isRequired,
  selectImageFile: PropTypes.func.isRequired,
  addStory: PropTypes.func.isRequired,
  editStory: PropTypes.func.isRequired,
  deleteStory: PropTypes.func.isRequired,
  setDatasetId: PropTypes.func.isRequired,
  setDatasetName: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Composer));
